import { format, startOfDay } from 'date-fns';
import { getServiceStaffAssignment } from '../../shared/booking/service-staff/serviceStaffRouting';
import { enrichStorefrontStaffFromPortalPattern } from '../storefront-staff-selection/enrichStorefrontStaffFromPortalPattern';
import AvailabilityService from '../storefront-client-portal/bundle/components/booking/services/AvailabilityService';
import {
  explainStorefrontStaffAssignment,
  isStorefrontBookingDebugEnabled,
  traceStorefrontBooking,
  warnStorefrontBooking,
} from './generateStorefrontTimeSlots';

const STAFF_ROSTER_CACHE_TTL_MS = 60_000;

const staffRosterCache = new Map();
const pendingPrefetches = new Map();

const resolveStorefrontCompanyLogoUrl = (companyData = {}) =>
  companyData.logoUrl ||
  companyData.logo ||
  companyData.logoURL ||
  companyData.imageUrl ||
  companyData.branding?.logoUrl ||
  companyData.settings?.logoUrl ||
  null;

const normalizeStorefrontPhotoURL = (value) => {
  if (value == null) return null;
  const s = String(value).trim();
  return s || null;
};

const storefrontStaffNeedsEnrichment = (member = {}) =>
  !member.photoURL || !member.firstName || !member.name;

export const computeStorefrontTotalDurationMinutes = (
  service,
  selectedPackage = null,
  selectedAddons = []
) => {
  let total = selectedPackage?.duration || service?.duration || 0;
  total += selectedPackage?.duration
    ? selectedPackage?.bufferTime || 0
    : service?.bufferTime || 0;
  (selectedAddons || []).forEach((addon) => {
    total += addon.duration || 0;
    total += addon.bufferTime || 0;
  });
  return total;
};

export const buildStorefrontDayAppointmentsKey = (companyId, date) =>
  `${companyId}|${format(date, 'yyyy-MM-dd')}`;

export const buildStorefrontStaffCacheKey = ({
  date,
  service,
  selectedPackage = null,
  selectedAddons = [],
  locationId = null,
}) => {
  if (!date || !service?.id) return null;
  const addonKey = (selectedAddons || [])
    .map((addon) => addon.id)
    .sort()
    .join(',');
  return [
    format(date, 'yyyy-MM-dd'),
    service.id,
    selectedPackage?.id || '',
    addonKey,
    computeStorefrontTotalDurationMinutes(service, selectedPackage, selectedAddons),
    locationId || '',
  ].join('|');
};

const buildPrefetchCacheKey = ({
  companyData,
  service,
  date,
  selectedPackage,
  selectedAddons,
  locationId = null,
}) =>
  [
    companyData?.id || '',
    buildStorefrontStaffCacheKey({
      date,
      service,
      selectedPackage,
      selectedAddons,
      locationId,
    }),
  ].join('|');

export const getCachedStorefrontStaffPrefetch = (cacheKey) => {
  if (!cacheKey) return null;
  const cached = staffRosterCache.get(cacheKey);
  if (!cached || Date.now() - cached.timestamp >= STAFF_ROSTER_CACHE_TTL_MS) {
    return null;
  }
  return cached;
};

const fetchStorefrontStaffRoster = async ({
  companyData,
  service,
  date,
  selectedPackage,
  selectedAddons,
  customerNoStaffMessage,
  locationId = null,
  location = null,
}) => {
  const {
    staff: eligibleStaff,
    serviceData,
    eligibleEmployeeIds,
    explicitEmployeeIds,
    companyStaffAtLocation,
  } = await getServiceStaffAssignment({
    companyData,
    serviceId: service.id,
    locationId,
    location,
  });

  const staffDebug = isStorefrontBookingDebugEnabled()
    ? {
      locationId,
      serviceLocationIds: serviceData?.locationIds || [],
      serviceEmployeeIds: explicitEmployeeIds || [],
      eligibleEmployeeIds,
      companyStaffAtLocationIds: (companyStaffAtLocation || []).map((member) => member.id),
      eligibleStaffCount: eligibleStaff.length,
    }
    : undefined;

  if (!eligibleStaff.length) {
    const reason = explainStorefrontStaffAssignment({
      locationId,
      serviceData,
      explicitEmployeeIds: explicitEmployeeIds || [],
      eligibleEmployeeIds,
      companyStaff: companyStaffAtLocation,
      staff: eligibleStaff,
    });
    traceStorefrontBooking('prefetch.noEligibleStaff', {
      serviceId: service.id,
      locationId,
      reason,
      staffDebug,
    });
    return { error: customerNoStaffMessage, staff: [], staffDebug };
  }

  traceStorefrontBooking('prefetch.staffRosterResolved', {
    serviceId: service.id,
    locationId,
    eligibleEmployeeIds,
    rosterCount: eligibleStaff.length,
    staffDebug,
  });

  const enrichedStaff = await Promise.all(
    eligibleStaff.map((member) =>
      storefrontStaffNeedsEnrichment(member)
        ? enrichStorefrontStaffFromPortalPattern({
            companyId: companyData.id,
            staffMember: member,
          })
        : member
    )
  );

  const storefrontLogo = normalizeStorefrontPhotoURL(resolveStorefrontCompanyLogoUrl(companyData));

  return {
    staff: [
      {
        id: 'any',
        firstName: 'Any',
        lastName: 'Available',
        name: 'Any Available',
        isAnyAvailable: true,
        photoURL: storefrontLogo,
      },
      ...enrichedStaff,
    ],
    staffDebug,
  };
};

export const prefetchStorefrontAvailability = async ({
  companyData,
  service,
  date = startOfDay(new Date()),
  selectedPackage = null,
  selectedAddons = [],
  customerNoStaffMessage = 'This service is temporarily unavailable online right now. Please choose another service or contact us for help.',
  locationId = null,
  location = null,
  includeAppointments = true,
}) => {
  if (!companyData?.id || !service?.id || !date) {
    traceStorefrontBooking('prefetch.skipped.missingParams', {
      companyId: companyData?.id,
      serviceId: service?.id,
      hasDate: Boolean(date),
    });
    return { staff: [], appointments: [], error: null };
  }

  const normalizedDate = startOfDay(date);
  const staffCacheKey = buildStorefrontStaffCacheKey({
    date: normalizedDate,
    service,
    selectedPackage,
    selectedAddons,
    locationId,
  });
  const prefetchCacheKey = buildPrefetchCacheKey({
    companyData,
    service,
    date: normalizedDate,
    selectedPackage,
    selectedAddons,
    locationId,
  });

  traceStorefrontBooking('prefetch.request', {
    prefetchCacheKey,
    staffCacheKey,
    locationId,
    serviceId: service.id,
    date: format(normalizedDate, 'yyyy-MM-dd'),
  });

  const cachedStaff = getCachedStorefrontStaffPrefetch(staffCacheKey);
  if (cachedStaff) {
    traceStorefrontBooking('prefetch.staffCacheHit', {
      staffCacheKey,
      rosterCount: (cachedStaff.staff || []).filter((member) => !member.isAnyAvailable).length,
      hasError: Boolean(cachedStaff.error),
    });
    return {
      staffCacheKey,
      dayAppointmentsKey: buildStorefrontDayAppointmentsKey(companyData.id, normalizedDate),
      staff: cachedStaff.staff,
      error: cachedStaff.error || null,
      appointments: includeAppointments
        ? await AvailabilityService.fetchDayAppointments(companyData.id, normalizedDate)
        : [],
    };
  }

  if (pendingPrefetches.has(prefetchCacheKey)) {
    traceStorefrontBooking('prefetch.deduped', { prefetchCacheKey });
    return pendingPrefetches.get(prefetchCacheKey);
  }

  const promise = (async () => {
    const appointmentsPromise = includeAppointments
      ? AvailabilityService.fetchDayAppointments(companyData.id, normalizedDate)
      : Promise.resolve([]);

    const [staffResult, appointments] = await Promise.all([
      fetchStorefrontStaffRoster({
        companyData,
        service,
        date: normalizedDate,
        selectedPackage,
        selectedAddons,
        customerNoStaffMessage,
        locationId,
        location,
      }),
      appointmentsPromise,
    ]);

    staffRosterCache.set(staffCacheKey, {
      staff: staffResult.staff,
      error: staffResult.error || null,
      timestamp: Date.now(),
    });

    const result = {
      staffCacheKey,
      dayAppointmentsKey: buildStorefrontDayAppointmentsKey(companyData.id, normalizedDate),
      staff: staffResult.staff,
      error: staffResult.error || null,
      staffDebug: staffResult.staffDebug || null,
      appointments,
    };

    traceStorefrontBooking('prefetch.finished', {
      prefetchCacheKey,
      rosterCount: (staffResult.staff || []).filter((member) => !member.isAnyAvailable).length,
      appointmentCount: appointments?.length || 0,
      hasError: Boolean(staffResult.error),
    });

    return result;
  })().finally(() => {
    pendingPrefetches.delete(prefetchCacheKey);
  });

  pendingPrefetches.set(prefetchCacheKey, promise);
  return promise;
};

export const prefetchStorefrontAvailabilityForToday = (params) =>
  prefetchStorefrontAvailability({
    ...params,
    date: startOfDay(new Date()),
  });
